import IAddress from "amnShared/addresses/models/IAddress";
import IStoreDto from "./IStoreDto";
import { ILocalization } from "adminPanel/preparedMaterials/IPreparedMaterials";
import {
  mapApiAddressToForm,
  mapStoreAddressToDto,
} from "./storeAddressHelpers";
import format from "date-fns/format";
import { NIL, v4 as uuid } from "uuid";
import { OrderType } from "models/common-props/TransactionTypes";
export interface IArea {
  id: string;
  name: string;
  countryId: string;
  countryName: string;
  code: string;
  subDivisionId: string;
  subDivisionName: string;
}

export const EmptyArea: IArea = {
  id: NIL,
  name: "",
  countryId: NIL,
  countryName: "",
  code: "",
  subDivisionId: NIL,
  subDivisionName: "",
};

export interface ISalesman {
  id: string;
  name: string;
}

export interface IStoreArea {
  id: string;
  storeId: string;
  area: IArea | undefined;
  delivery: boolean;
  deliveryFees: number;
  useFreeDeliveryForHighValueOrders?: boolean;
  minForFreeDelivery?: number;
}

export const EmptyStoreArea: IStoreArea = {
  id: NIL,
  storeId: "",
  area: EmptyArea,
  delivery: true,
  deliveryFees: 0,
  useFreeDeliveryForHighValueOrders: false,
  minForFreeDelivery: 0,
};

export interface IDriver {
  id: string;
  name: string;
}

export interface IStoreDriver {
  id: string;
  isActive: boolean;
  storeId: string;
  driver?: IDriver;
}
export const Emptydriver: IDriver = {
  id: NIL,
  name: "",
};

export const EmptyStoreDriver: IStoreDriver = {
  id: NIL,
  isActive: true,
  storeId: NIL,
  driver: Emptydriver,
};

export interface IStoreSalesman {
  id: string;
  storeId: string;
  salesman?: ISalesman;
  isActive: boolean;
  isDefault: boolean;
}

export const EmptyStoreSalesman: IStoreSalesman = {
  id: NIL,
  storeId: NIL,
  salesman: undefined,
  isDefault: false,
  isActive: true,
};

export interface IChildStore {
  id: string;
  name: string;
  isStoreIsRestaurant?: boolean;
}
export interface IOtherStore {
  id: string;
  parentStoreId: string;
  childStore?: IChildStore;
}
export const EmptyIChildStore: IChildStore = {
  id: NIL,
  name: "",
};
export const EmptyOtherStore: IOtherStore = {
  id: NIL,
  parentStoreId: NIL,
  childStore: EmptyIChildStore,
};

export interface IStoreAutoComplete {
  id: string;
  name: string;
}

export const emptyAutoCompleteStore: IStoreAutoComplete = {
  id: NIL,
  name: "",
};

export interface IStorePrinter {
  id: string;
  name: string;
  printerName: string;
  paperSize: string;
  storeId: string;
}

export const EmptyStorePrinter: IStorePrinter = {
  id: NIL,
  name: "",
  printerName: "",
  paperSize: "",
  storeId: "",
};

export interface IStorePrinterCustomizations {
  id: string;
  name: string;
  categoryIds: string[];
  categoryName?: string;
  storePrinterId: string;
  printerName?: string;
  printDesignId: string;
  printDesignName?: string;
  printDuplicateCopyLabel: boolean;
  storeId: string;
  orderTypes: OrderType[];
  sectionIds: string[];
}

export const EmptyStorePrinterCustomizations: IStorePrinterCustomizations = {
  id: NIL,
  name: "",
  categoryIds: [],
  categoryName: "",
  storePrinterId: "",
  printerName: "",
  printDesignId: "",
  printDesignName: "",
  printDuplicateCopyLabel: true,
  storeId: NIL,
  orderTypes: [],
  sectionIds: [],
};

export interface IPreparationServingSettings {
  isSalesReasonMandatory: boolean;
  usePreparation: boolean;
  useSendToKitchenCommand: boolean;
  allowAssignDriverBeforePreparationComplete: boolean;
  useServing: boolean;
  autoClearTableOnOrderCompletion: boolean;
  useExpectedPreparationTime: boolean;
  useOrderStatusScreenOSSS: boolean;
  orderQueueNumber: boolean;
  dailyStartingQueueNumber: number;
  resetQueueNumberAt: number;
  bufferTime: number;
}

export const EmptyPreparationServingSettings: IPreparationServingSettings = {
  isSalesReasonMandatory: false,
  usePreparation: false,
  useSendToKitchenCommand: false,
  allowAssignDriverBeforePreparationComplete: false,
  useServing: false,
  autoClearTableOnOrderCompletion: false,
  useExpectedPreparationTime: false,
  useOrderStatusScreenOSSS: false,
  orderQueueNumber: false,
  dailyStartingQueueNumber: 1,
  resetQueueNumberAt: 100,
  bufferTime: 5,
};

export interface IItemsDisplaySettings {
  groupSimilarItemsInPosScreen: boolean;
  groupSimilarItemsInKitchenAndCustomizationPrint: boolean;
  groupSimilarItemsInCustomerReceiptPrint: boolean;
}

export const EmptyItemsDisplaySettings: IItemsDisplaySettings = {
  groupSimilarItemsInPosScreen: true,
  groupSimilarItemsInKitchenAndCustomizationPrint: true,
    groupSimilarItemsInCustomerReceiptPrint: true,
};

export interface ITableMapIdleSettings {
  showLastOrderActivityOnTableMap: boolean;
  idleAlertAfterMinutes: number;
}

export const EmptyTableMapIdleSettings: ITableMapIdleSettings = {
  showLastOrderActivityOnTableMap: true,
  idleAlertAfterMinutes: 15,
};

export default interface IStore {
  id: string;
  localizations: ILocalization[];
  printingServiceUrl: string;
  useReturnCoupons: boolean;
  returnCouponeValidFor: number;
  unidentifiedAreas?: boolean;
  deliveryFees?: number;
  useFreeDeliveryForOrderForUniddentifiedAreas?: boolean;
  minForDeliveryForUnidentifiedAreas?: number;
  isStoreIsRestaurant?: boolean;
  useFastService?: boolean;
  useFullService?: boolean;
  deliveryFromStore?: boolean;
  isDeliveryWorkingTimeBounded: boolean;
  preparationAndServingSettings: IPreparationServingSettings;
  itemsDisplaySettings: IItemsDisplaySettings;
  tableMapIdleSettings: ITableMapIdleSettings;
  deliveryFromTime: Date | string | null;
  deliveryToTime: Date | string | null;
  storeAreas: IStoreArea[];
  storeDrivers: IStoreDriver[];
  storeSalesmen: IStoreSalesman[];
  childernStores: IOtherStore[];
  storePrinters: IStorePrinter[];
  storePrinterCustomizations: IStorePrinterCustomizations[];
  posStationNumber?: number | null;
  starClassification?: number;
  storeCode?: string | null;
  address?: IAddress | null;
}

export function mapStoreToDto(store: IStore): IStoreDto {
  const oldToNewPrinterIdMap = new Map<string, string>();

  const storeDto: IStoreDto = {
    id: store.id,
    localizations: store.localizations,
    returnCouponeValidFor: store.returnCouponeValidFor,
    useReturnCoupons: store.useReturnCoupons,
    printingServiceUrl: store.printingServiceUrl,
    unidentifiedAreas: store.unidentifiedAreas,
    deliveryFees: store.deliveryFees,
    useFreeDeliveryForOrderForUniddentifiedAreas:
      store.useFreeDeliveryForOrderForUniddentifiedAreas,
    minForDeliveryForUnidentifiedAreas:
      store.minForDeliveryForUnidentifiedAreas,
    isStoreIsRestaurant: store.isStoreIsRestaurant,
    useFastService: store.useFastService,
    useFullService: store.useFullService,
    deliveryFromStore: store.deliveryFromStore,
    posStationNumber: store.posStationNumber,
    starClassification: store.starClassification,
    storeCode: store.storeCode ?? null,
    address: mapStoreAddressToDto(store.address),
    preparationAndServingSettings: store.preparationAndServingSettings
      ? store.preparationAndServingSettings
      : EmptyPreparationServingSettings,
    itemsDisplaySettings: store.itemsDisplaySettings
      ? {
        ...store.itemsDisplaySettings,
        ...(store.itemsDisplaySettings.groupSimilarItemsInPosScreen
          ? {
            groupSimilarItemsInKitchenAndCustomizationPrint: true,
            groupSimilarItemsInCustomerReceiptPrint: true,
          }
          : {}),
      }
      : EmptyItemsDisplaySettings,
    tableMapIdleSettings: store.tableMapIdleSettings
      ? store.tableMapIdleSettings
      : EmptyTableMapIdleSettings,
    storeAreas: store.storeAreas.map((area: IStoreArea) => ({
      id: uuid(),
      storeId: store.id,
      areaId: area.area ? area.area.id : NIL,
      delivery: area.delivery,
      deliveryFees: area.deliveryFees,
      useFreeDeliveryForHighValueOrders: area.useFreeDeliveryForHighValueOrders,
      minForFreeDelivery: area.minForFreeDelivery,
    })),

    storeDrivers: store.storeDrivers.map((driver: IStoreDriver) => ({
      id: uuid(),
      isActive: driver.isActive,
      storeId: store.id,
      driverId: driver.driver ? driver.driver.id : NIL,
    })),

    storeSalesmen: store.storeSalesmen.map((salesman: IStoreSalesman) => ({
      id: uuid(),
      storeId: salesman.storeId,
      salesmanId: salesman.salesman ? salesman.salesman.id : NIL,
      isDefault: salesman.isDefault,
      isActive: salesman.isActive,
    })),

    childernStores: store.childernStores.map((childStore: any) => ({
      parentStoreId: childStore.parentStoreId,
      childStoreId: childStore.childStore ? childStore.childStore.id : NIL,
    })),

    storePrinters: store.storePrinters.map((printer: IStorePrinter) => {
      const newId = uuid();
      oldToNewPrinterIdMap.set(
        printer.id,
        store.id === NIL ? newId : printer.id,
      );
      return {
        id: store.id === NIL ? newId : printer.id,
        name: printer.name,
        printerName: printer.printerName,
        paperSize: printer.paperSize,
        storeId: printer.storeId,
      };
    }),

    storePrinterCustomizations: store.storePrinterCustomizations.map(
      (customization: IStorePrinterCustomizations) => ({
        id: uuid(),
        name: customization.name,
        categoryIds: customization.categoryIds,
        storePrinterId:
          oldToNewPrinterIdMap.get(customization.storePrinterId) ||
          customization.storePrinterId,
        printDesignId: customization.printDesignId || undefined,
        printDuplicateCopyLabel: customization.printDuplicateCopyLabel,
        storeId: customization.storeId,
        orderTypes: customization.orderTypes,
        sectionIds: customization.sectionIds,
      }),
    ),
    isDeliveryWorkingTimeBounded: store.isDeliveryWorkingTimeBounded,
    deliveryFromTime: store.isDeliveryWorkingTimeBounded
      ? format(new Date(store.deliveryFromTime!), "HH:mm")
      : null,
    deliveryToTime: store.isDeliveryWorkingTimeBounded
      ? format(new Date(store.deliveryToTime!), "HH:mm")
      : null,
  };

  return storeDto;
}

export function mapDtoToStore(storeDto: IStoreDto): IStore {
  const store: IStore = {
    id: storeDto.id,
    returnCouponeValidFor: storeDto.returnCouponeValidFor,
    useReturnCoupons: storeDto.useReturnCoupons,
    unidentifiedAreas: storeDto?.unidentifiedAreas,
    deliveryFees: storeDto.deliveryFees,
    useFreeDeliveryForOrderForUniddentifiedAreas:
      storeDto.useFreeDeliveryForOrderForUniddentifiedAreas,
    minForDeliveryForUnidentifiedAreas:
      storeDto.minForDeliveryForUnidentifiedAreas,
    printingServiceUrl: storeDto.printingServiceUrl,
    address: mapApiAddressToForm(storeDto.address),
    isStoreIsRestaurant: storeDto.isStoreIsRestaurant,
    useFastService: storeDto.useFastService,
    useFullService: storeDto.useFullService,
    posStationNumber: storeDto.posStationNumber,
    starClassification: storeDto.starClassification,
    storeCode: storeDto.storeCode ?? null,
    preparationAndServingSettings: storeDto.preparationAndServingSettings
      ? storeDto.preparationAndServingSettings
      : EmptyPreparationServingSettings,
    itemsDisplaySettings: storeDto.itemsDisplaySettings
      ? {
        ...EmptyItemsDisplaySettings,
        ...storeDto.itemsDisplaySettings,
      }
      : EmptyItemsDisplaySettings,
    tableMapIdleSettings: storeDto.tableMapIdleSettings
      ? {
        ...EmptyTableMapIdleSettings,
        ...storeDto.tableMapIdleSettings,
      }
      : EmptyTableMapIdleSettings,
    storeAreas: storeDto.storeAreas
      ? storeDto.storeAreas.map((area) => ({
        id: area.id,
        storeId: area.storeId,
        area: {
          id: area.areaId,
          name: area.areaName || "",
          countryId: "",
          countryName: area.countryName || "",
          code: "",
          subDivisionId: "",
          subDivisionName: area.subdivisionName || "",
        },
        delivery: area.delivery,
        deliveryFees: area.deliveryFees,
        useFreeDeliveryForHighValueOrders:
          area.useFreeDeliveryForHighValueOrders,
        minForFreeDelivery: area.minForFreeDelivery,
      }))
      : [],
    storeDrivers: storeDto.storeDrivers
      ? storeDto.storeDrivers.map((driver) => ({
        id: driver.id,
        storeId: driver.storeId,
        isActive: driver.isActive,
        driver: {
          id: driver.driverId,
          name: driver.driverName || "",
        },
      }))
      : [],
    storeSalesmen: storeDto.storeSalesmen
      ? storeDto.storeSalesmen.map((salesman) => ({
        id: salesman.id,
        storeId: salesman.storeId,
        salesman: {
          id: salesman.salesmanId,
          name: salesman.salesmanName || "",
        },
        isDefault: salesman.isDefault,
        isActive: salesman.isActive,
      }))
      : [],
    childernStores: storeDto.childernStores
      ? storeDto.childernStores.map((childStore: any) => ({
        id: childStore.childStoreId,
        parentStoreId: childStore.parentStoreId,
        childStore: {
          id: childStore.childStoreId,
          name:
            childStore.localizations?.find((loc: any) => loc.default)?.name ||
            childStore.localizations?.[0]?.name ||
            "",
        },
      }))
      : [],
    storePrinters: storeDto.storePrinters
      ? storeDto.storePrinters.map((printer) => ({
        id: printer.id,
        name: printer.name,
        printerName: printer.printerName,
        paperSize: printer.paperSize,
        storeId: printer.storeId,
      }))
      : [],
    storePrinterCustomizations: storeDto.storePrinterCustomizations
      ? storeDto.storePrinterCustomizations.map((customization) => ({
        id: customization.id,
        name: customization.name,
        categoryIds: customization.categoryIds,
        categoryName: customization.categoryName,
        storePrinterId: customization.storePrinterId,
        printerName:
          customization.printerName ||
          storeDto.storePrinters?.find(
            (p) => p.id === customization.storePrinterId,
          )?.name ||
          "",
        printDesignId: customization.printDesignId || "",
        printDesignName: customization.printDesignName,
        printDuplicateCopyLabel:
          customization.printDuplicateCopyLabel ?? true,
        storeId: customization.storeId,
        orderTypes: customization.orderTypes || [],
        sectionIds: customization.sectionIds || [],
      }))
      : [],
    localizations: storeDto.localizations
      ? storeDto.localizations.map((name) => ({
        id: name.id,
        name: name.name,
        default: name.default,
        languageCode: name.languageCode,
      }))
      : [],
    deliveryFromStore: storeDto.deliveryFromStore,
    isDeliveryWorkingTimeBounded: storeDto.isDeliveryWorkingTimeBounded,
    deliveryFromTime: storeDto.deliveryFromTime,
    deliveryToTime: storeDto.deliveryToTime,
  };

  return store;
}
